import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Banner from "../Components/Banner";
import ServiceCard from "../Components/ServiceCard";
import { servicesPageData } from "../Constants/data.jsx";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight } from "react-bootstrap-icons";
import { useServices } from "../hooks/useServices";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const Services = () => {
  const navigate = useNavigate();

  // Use the services hook - API ONLY
  const { services } = useServices();

  // Loading state
  const [loading, setLoading] = useState(true);

  // Temple Services style state
  const [isMobile, setIsMobile] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const rowRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Simulate loading for better UX
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800); // 0.8 second delay for skeleton visibility

    return () => clearTimeout(timer);
  }, []);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // UPDATE: The handleServiceClick function
  const handleServiceClick = (serviceSlug) => {
    // CHANGE: Accept slug directly
    navigate(`/service/${serviceSlug}`); // USE only slug
  };

  const handleNext = () => {
    if (isMobile && services.length > 0) {
      setCurrentIndex((i) => (i + 1) % services.length);
    }
  };

  const handlePrev = () => {
    if (isMobile && services.length > 0) {
      setCurrentIndex((i) => (i - 1 + services.length) % services.length);
    }
  };

  // Swipe controls
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.touches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.touches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    if (touchStart - touchEnd > 50) handleNext();
    if (touchStart - touchEnd < -50) handlePrev();
  };

  // Skeleton items for desktop (3 per row)
  const desktopSkeletonItems = Array.from({ length: 3 }, (_, index) => (
    <motion.div
      key={`desktop-skeleton-${index}`}
      className="col-md-4 d-flex"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <div className="w-100 h-100">
        {/* Service Card Skeleton */}
        <div className="card shadow-sm border-0 h-100">
          <div className="card-body p-4">
            {/* Image skeleton */}
            <Skeleton
              height={200}
              width="100%"
              style={{ borderRadius: "10px", marginBottom: "20px" }}
            />

            {/* Title skeleton */}
            <Skeleton
              height={30}
              width="80%"
              style={{ marginBottom: "15px" }}
            />

            {/* Description skeleton */}
            <Skeleton
              height={20}
              width="100%"
              style={{ marginBottom: "10px" }}
            />
            <Skeleton
              height={20}
              width="90%"
              style={{ marginBottom: "10px" }}
            />
            <Skeleton
              height={20}
              width="70%"
              style={{ marginBottom: "20px" }}
            />

            {/* Button skeleton */}
            <Skeleton
              height={40}
              width="60%"
              style={{ borderRadius: "20px" }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  ));

  // Single skeleton for mobile carousel
  const mobileSkeleton = (
    <motion.div
      key="mobile-skeleton"
      className="d-flex justify-content-center w-100"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <div
        className="card shadow-sm border-0"
        style={{ width: "100%", maxWidth: 420, height: 420 }}
      >
        <div className="card-body p-4">
          {/* Image skeleton */}
          <Skeleton
            height={200}
            width="100%"
            style={{ borderRadius: "10px", marginBottom: "20px" }}
          />

          {/* Title skeleton */}
          <Skeleton height={30} width="80%" style={{ marginBottom: "15px" }} />

          {/* Description skeleton */}
          <Skeleton height={20} width="100%" style={{ marginBottom: "10px" }} />
          <Skeleton height={20} width="90%" style={{ marginBottom: "10px" }} />
          <Skeleton height={20} width="70%" style={{ marginBottom: "20px" }} />

          {/* Button skeleton */}
          <Skeleton height={40} width="60%" style={{ borderRadius: "20px" }} />
        </div>
      </div>
    </motion.div>
  );

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title> Services - Sydney Sri Ayyappa Swami Centre</title>
        <meta
          name="description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta
          name="keywords"
          content="Sydney Sri Ayyappa Swami Centre, Ayyappa Temple Sydney, Lord Ayyappa Temple Australia, Hindu Temple in Sydney, Ayyappa Pooja Sydney, Swami Ayyappa Devotees Sydney, Sabarimala Ayyappa Sydney, Hindu Religious Centre Sydney, Ayyappa Bhajans Sydney, Indian Hindu Temple Sydney"
        />

        {/* Open Graph */}
        <meta property="og:title" content="Sydney Sri Ayyappa Swami Centre" />
        <meta
          property="og:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sydneysriayyappa.com/" />

        {/* Social Links */}
        <meta
          property="og:see_also"
          content="https://www.youtube.com/@SydneySriAyyappaSwamiCentre"
        />
        <meta
          property="og:see_also"
          content="https://www.instagram.com/sydneysriayyappa/"
        />
        <meta
          property="og:see_also"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Facebook  */}

        <meta
          property="fb:admins"
          content="https://www.facebook.com/sydneyayyappa"
        />

        {/* Instagram */}
        <meta
          name="instagram:title"
          content="Sydney Sri Ayyappa Swami Centre"
        />
        <meta
          name="instagram:description"
          content="Sydney Sri Ayyappa Swami Centre is a spiritual hub for Lord Ayyappa devotees, offering poojas, bhajans, festivals, and community services in Sydney."
        />

        <meta name="instagram:site" content="@sydneysriayyappa" />
      </Helmet>

      <div className="container-fluid p-0">
        {/* Banner Section */}
        <Banner bannerData={servicesPageData} />

        {/* Services Section - WITH SKELETON LOADING */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="container-fluid text-center py-5">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-12">
                    {/* HEADINGS */}
                    <motion.h4
                      className="text-uppercase fw-bold heading"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      {loading ? (
                        <Skeleton width={300} height={30} />
                      ) : (
                        "Enhancing Lives with Spiritual Guidance"
                      )}
                    </motion.h4>

                    <motion.h1
                      className="my-3 fw-bold service-title"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      {loading ? (
                        <Skeleton width={500} height={40} />
                      ) : (
                        "Connecting Devotees Through Spiritual Temple Services"
                      )}
                    </motion.h1>

                    {/* WRAPPER */}
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div className="position-relative">
                        {/* SHOW SKELETONS WHILE LOADING */}
                        {loading ? (
                          <>
                            {/* MOBILE SKELETON */}
                            {isMobile ? (
                              <div
                                className="d-flex justify-content-center w-100"
                                ref={rowRef}
                                style={{ touchAction: "pan-y" }}
                              >
                                {mobileSkeleton}
                              </div>
                            ) : (
                              // DESKTOP SKELETONS
                              <div className="row justify-content-center align-items-stretch g-4">
                                {desktopSkeletonItems}
                              </div>
                            )}

                            {/* MOBILE NAV ARROWS SKELETON */}
                            {isMobile && (
                              <motion.div
                                className="d-flex justify-content-center gap-3 mt-3"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                              >
                                <Skeleton
                                  circle
                                  width={50}
                                  height={50}
                                  style={{ marginRight: "10px" }}
                                />
                                <Skeleton circle width={50} height={50} />
                              </motion.div>
                            )}
                          </>
                        ) : (
                          /* SHOW ACTUAL CONTENT WHEN LOADED */
                          <>
                            {/* MOBILE CAROUSEL */}
                            {isMobile ? (
                              <div
                                className="d-flex justify-content-center w-100"
                                ref={rowRef}
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                style={{ touchAction: "pan-y" }}
                              >
                                <motion.div
                                  key={currentIndex}
                                  className="d-flex justify-content-center w-100"
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  transition={{ duration: 0.25 }}
                                >
                                  {services.length > 0 ? (
                                    <ServiceCard
                                      service={services[currentIndex]}
                                      onClick={() =>
                                        handleServiceClick(
                                          services[currentIndex].slug
                                        )
                                      }
                                      style={{
                                        width: "100%",
                                        maxWidth: 420,
                                        height: 420,
                                      }}
                                    />
                                  ) : (
                                    <div className="text-center py-5">
                                      <p className="text-muted">
                                        We are preparing our spiritual services
                                        for you. Please stay tuned.
                                      </p>
                                    </div>
                                  )}
                                </motion.div>
                              </div>
                            ) : (
                              // DESKTOP GRID
                              <div className="row justify-content-center align-items-stretch g-4">
                                {services.length > 0 ? (
                                  services.map((service, index) => (
                                    <motion.div
                                      key={service.id}
                                      className="col-md-4 d-flex"
                                      initial={{ opacity: 0, y: 30 }}
                                      whileInView={{ opacity: 1, y: 0 }}
                                      transition={{ delay: index * 0.1 }}
                                    >
                                      <ServiceCard
                                        service={service}
                                        onClick={() =>
                                          handleServiceClick(service.slug)
                                        }
                                        className="w-100 h-100"
                                      />
                                    </motion.div>
                                  ))
                                ) : (
                                  <div className="text-center py-5">
                                    <p className="text-muted">
                                      We are preparing our spiritual services
                                      for you. Please stay tuned.
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* MOBILE NAV ARROWS - Only show when services exist */}
                            {isMobile && services.length > 1 && (
                              <motion.div
                                className="d-flex justify-content-center gap-3 mt-3"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                              >
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handlePrev}
                                >
                                  <ArrowLeft className="fs-5 heading" />
                                </div>
                                <div
                                  className="custom-border d-flex align-items-center justify-content-center rounded-circle cursor-pointer p-2"
                                  onClick={handleNext}
                                >
                                  <ArrowRight className="fs-5 heading" />
                                </div>
                              </motion.div>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Services;
